import { useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { FiChevronRight, FiPlus, FiSearch } from "react-icons/fi";
import { api, createOLT, getOverview, OVERVIEW_REFRESH_MS, updateOLT, type OLTForm } from "@/lib/api";
import type { OLT, Overview } from "@/lib/types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { ageOf, OLT_STATE_RANK, OltStateBadge, OnlineBar } from "@/components/OltStatus";
import { oltState, type OltState, type Reason } from "@/lib/olthealth";
import { fmtNum, useI18n, type Key } from "@/lib/i18n";
import { cn } from "@/lib/utils";

// Blank = "leave as is" on edit, and the API fills protocol/port from driver on create.
const empty: OLTForm = { name: "", mgmt_ip: "", mgmt_username: "", driver: "cdata" };

export function OLTDialog({ edit, onClose }: { edit: OLT | null; onClose: () => void }) {
  const { t } = useI18n();
  const qc = useQueryClient();
  const [form, setForm] = useState<OLTForm>(
    edit
      ? { name: edit.name, mgmt_ip: edit.mgmt_ip, mgmt_port: edit.mgmt_port, mgmt_username: "", driver: edit.driver, model: edit.model, location: edit.location ?? "" }
      : empty,
  );
  const save = useMutation({
    mutationFn: (body: OLTForm) => (edit ? updateOLT(edit.id, body) : createOLT(body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["olts"] });
      onClose();
    },
  });
  const field = (key: keyof OLTForm, label: string, type = "text") => (
    <label className="block text-xs text-slate-400">
      {label}
      <Input
        type={type}
        value={String(form[key] ?? "")}
        onChange={(e) => setForm({ ...form, [key]: key === "mgmt_port" ? Number(e.target.value) || undefined : e.target.value })}
      />
    </label>
  );
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{edit ? t("olts.editTitle", { name: edit.name }) : t("olts.add")}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-3 sm:grid-cols-2">
          {field("name", t("common.name"))}
          {field("mgmt_ip", t("olts.mgmtIp"))}
          <label className="block text-xs text-slate-400">
            {t("olts.driver")}
            <select
              className="h-9 w-full rounded-md border border-input bg-transparent px-2 text-sm"
              value={form.driver}
              onChange={(e) => setForm({ ...form, driver: e.target.value, mgmt_port: undefined })}
            >
              <option value="cdata">cdata (SSH 22)</option>
              <option value="hsgq">hsgq (telnet 23)</option>
              <option value="tenda">tenda (REST https 8443)</option>
            </select>
          </label>
          {field("mgmt_port", t("olts.port"), "number")}
          {field("mgmt_username", t("olts.cliUser"))}
          {field("mgmt_password", t(edit ? "olts.cliPassKeep" : "olts.cliPass"), "password")}
          {field("enable_password", t("olts.enablePass"), "password")}
          {field("location", t("olts.location"))}
        </div>
        {save.error && <p className="text-signal-crit">{(save.error as Error).message}</p>}
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>{t("common.cancel")}</Button>
          <Button
            disabled={save.isPending || !form.name || !form.mgmt_ip || (!edit && !form.mgmt_username)}
            onClick={() => save.mutate(form)}
          >
            {t(save.isPending ? "common.saving" : "common.save")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** One fleet row: the OLT's inventory joined with its live posture from the overview. */
type Row = {
  olt: OLT;
  live: Overview["olts"][number] | undefined;
  pons: number;
  state: OltState;
  reasons: Reason[];
};

type Filter = "all" | "abnormal" | OltState;

export default function OLTsPage() {
  const { t, locale } = useI18n();
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<{ edit: OLT | null } | null>(null);
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<Filter>("all");
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const list = useQuery({ queryKey: ["olts"], queryFn: () => api<{ olts: OLT[] }>("/api/v1/olts"), refetchInterval: 30_000 });
  // Posture (ONT counts, PONs, alerts, temperature) is the overview's: one cached poll for the whole panel.
  const ov = useQuery({ queryKey: ["overview"], queryFn: getOverview, refetchInterval: OVERVIEW_REFRESH_MS });

  const rows: Row[] = useMemo(() => {
    const pollS = ov.data?.olt_poll_interval_s ?? 120;
    return (list.data?.olts ?? []).map((olt) => {
      const live = ov.data?.olts.find((o) => o.id === olt.id);
      const pons = ov.data?.pons.filter((p) => p.olt_id === olt.id) ?? [];
      const { state, reasons } = oltState(
        { status: olt.status, last_polled_at: olt.last_polled_at, onts_total: live?.onts_total ?? 0,
          onts_online: live?.onts_online ?? 0, temp_c: live?.temp_c ?? null },
        pons, pollS, now,
      );
      return { olt, live, pons: pons.length, state, reasons };
    }).sort((a, b) => OLT_STATE_RANK[a.state] - OLT_STATE_RANK[b.state] || a.olt.name.localeCompare(b.olt.name));
  }, [list.data, ov.data, now]);

  const counts = rows.reduce<Record<OltState, number>>((m, r) => ({ ...m, [r.state]: (m[r.state] ?? 0) + 1 }),
    { healthy: 0, degraded: 0, critical: 0, down: 0, stale: 0, unknown: 0 });
  const needle = q.trim().toLowerCase();
  const shown = rows.filter((r) =>
    (filter === "all" || (filter === "abnormal" ? r.state !== "healthy" : r.state === filter)) &&
    (!needle || [r.olt.name, r.olt.mgmt_ip, r.olt.location, r.olt.vendor, r.olt.model].some((v) => v?.toLowerCase().includes(needle))));

  const chips: { f: Filter; label: string; n: number }[] = [
    { f: "all", label: t("olt.fleet.all"), n: rows.length },
    { f: "abnormal", label: t("olt.fleet.abnormal"), n: rows.length - counts.healthy },
    ...(["down", "critical", "stale", "degraded"] as OltState[]).filter((s) => counts[s] > 0)
      .map((s) => ({ f: s as Filter, label: t(`olt.state.${s}` as Key), n: counts[s] })),
  ];

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-100">{t("olt.fleet.title")}</h1>
          <p className="data mt-1 text-[12.5px] text-slate-400">
            {t("olt.fleet.summary", { n: fmtNum(locale, rows.length), healthy: fmtNum(locale, counts.healthy) })}
          </p>
        </div>
        <Button size="sm" onClick={() => setDialog({ edit: null })}><FiPlus /> {t("olts.add")}</Button>
      </div>

      {rows.length > 3 && (
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-64">
            <FiSearch className="pointer-events-none absolute top-1/2 left-2.5 size-3.5 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("olt.fleet.search")} className="h-8 border-rule bg-panel pl-8 text-[12.5px]" />
          </div>
          {chips.map((c) => (
            <button key={c.f} type="button" onClick={() => setFilter(c.f)}
              className={cn("rounded-sm border px-2 py-1 text-[12px] transition-colors",
                filter === c.f ? "border-signal/50 bg-signal/10 text-slate-100" : "border-rule text-slate-400 hover:text-slate-100")}>
              {c.label} <span className="data text-slate-500">{c.n}</span>
            </button>
          ))}
        </div>
      )}

      {(list.isLoading || ov.isLoading) && <p className="text-slate-400">{t("common.loading")}</p>}
      {list.error && <p className="text-signal-crit">{(list.error as Error).message}</p>}

      {list.data && (
        <div className="overflow-x-auto rounded-lg border border-rule bg-panel">
          <table className="w-full min-w-[900px] text-[13px]">
            <thead>
              <tr className="border-b border-rule text-left">
                {(["olt.col.olt", "olt.col.health", "olt.col.pon", "olt.col.ont", "olt.col.offline", "olt.col.warn", "olt.col.lastPoll"] as Key[]).map((k, i) => (
                  <th key={k} className={cn("eyebrow px-4 py-2.5 font-medium", i >= 2 && "text-right", i === 3 && "w-44")}>{t(k)}</th>
                ))}
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {shown.map(({ olt, live, pons, state, reasons }) => {
                const offline = live ? live.onts_total - live.onts_online : null;
                const pollS = ov.data?.olt_poll_interval_s ?? 120;
                const polledS = olt.last_polled_at ? (now - Date.parse(olt.last_polled_at)) / 1000 : null;
                return (
                  <tr key={olt.id} onClick={() => navigate(`/olts/${olt.id}`)}
                    className="group cursor-pointer border-b border-rule/70 last:border-b-0 hover:bg-white/[0.02]">
                    <td className="px-4 py-2.5">
                      <Link to={`/olts/${olt.id}`} onClick={(e) => e.stopPropagation()}
                        className="font-medium text-slate-100 group-hover:text-signal">{olt.name}</Link>
                      <div className="data mt-0.5 truncate text-[11.5px] text-slate-500">
                        {[olt.location, olt.mgmt_ip, [olt.vendor, olt.model].filter(Boolean).join(" · ")].filter(Boolean).join("  ·  ")}
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <OltStateBadge state={state} />
                      {reasons.length > 0 && state !== "healthy" && (
                        <div className="mt-1 text-[11.5px] text-slate-400">{reasons.map((r) => t(`olt.reason.${r}` as Key)).join(" · ")}</div>
                      )}
                    </td>
                    <td className="data px-4 py-2.5 text-right text-slate-200">{live ? fmtNum(locale, pons) : "—"}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="data text-slate-100">{live ? `${fmtNum(locale, live.onts_online)}/${fmtNum(locale, live.onts_total)}` : "—"}</div>
                      {live && live.onts_total > 0 && <div className="mt-1 ml-auto w-28"><OnlineBar online={live.onts_online} total={live.onts_total} /></div>}
                    </td>
                    <td className={cn("data px-4 py-2.5 text-right", offline ? "text-signal-crit" : "text-slate-600")}>
                      {offline ? fmtNum(locale, offline) : "—"}
                    </td>
                    <td className={cn("data px-4 py-2.5 text-right", live?.alerts_warn || live?.alerts_critical ? "text-signal-warn" : "text-slate-600")}>
                      {live && (live.alerts_warn || live.alerts_critical) ? fmtNum(locale, live.alerts_warn + live.alerts_critical) : "—"}
                    </td>
                    <td className={cn("data px-4 py-2.5 text-right", polledS !== null && polledS > pollS * 2 ? "text-signal-warn" : "text-slate-400")}>
                      {olt.last_polled_at ? t("olt.ago", { age: ageOf(olt.last_polled_at, now) }) : t("olt.never")}
                    </td>
                    <td className="pr-3 text-slate-600 group-hover:text-signal"><FiChevronRight /></td>
                  </tr>
                );
              })}
              {shown.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-slate-500">{t(rows.length ? "olt.fleet.noMatch" : "olts.empty")}</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
      {dialog && <OLTDialog edit={dialog.edit} onClose={() => setDialog(null)} />}
    </div>
  );
}
